// Simple / Advanced UI mode — ported from legacy toggleUIMode/applyUIMode
// (v20 lines ~1331-1366) and the handleAccess default (~1176-1180).
//
// The mode is store-driven in 2.0 (SLICES.UI.mode) — there is no body-class
// CSS switch; the registry's visibleRoutes() gates the OPERATE tier off the
// same value, so sidebar, routing and (later) per-view surfaces cannot drift.
//
// STORAGE KEYS ARE SHARED WITH v_20 ON PURPOSE: both apps run against the
// same DB during the parallel phase, often in the same browser. Using the
// exact legacy keys means a nurse's mode choice follows her between v_20 and
// 2.0 instead of resetting at the parallel URL.

import { canAssign } from './auth.js';

// Per-user key first (shared training laptops), then the shared legacy key —
// exact legacy read order (v20 line 1178). Writes touch ONLY the per-user key
// (legacy toggleUIMode line 1343 never wrote the fallback key).
export const MODE_KEY_PREFIX = 'tumaini_ui_mode_';
export const MODE_KEY_LEGACY = 'tumaini_ui_mode';

/**
 * The per-user storage key. Falls back to 'anon' exactly like legacy
 * (`CU&&CU.id||'anon'`).
 * @param {string|null|undefined} uid auth user id
 * @returns {string}
 */
export function modeKey(uid) {
  return MODE_KEY_PREFIX + (uid || 'anon');
}

/**
 * Role default when nothing valid is stored. TASK-2 ruling (widens legacy):
 * nurses default to simple; admin/programme-manager AND coordinator roles
 * default to advanced ('full'). Legacy defaulted only isAdmin roles to full
 * (v20 line 1179) — coordinators are promoted because they oversee the whole
 * queue and need the OPERATE tier daily; recorded here, not silent.
 * @param {string|null|undefined} role app_users.role
 * @returns {'simple'|'full'}
 */
export function defaultMode(role) {
  return canAssign(role) ? 'full' : 'simple';
}

/**
 * Pure resolution: a stored 'simple'/'full' wins; anything else (null,
 * corruption) falls to the role default — exact legacy validation
 * (`if(uiMode!=='simple'&&uiMode!=='full')…`, v20 line 1179).
 * @param {string|null|undefined} stored raw stored value
 * @param {string|null|undefined} role app_users.role
 * @returns {'simple'|'full'}
 */
export function resolveMode(stored, role) {
  return stored === 'simple' || stored === 'full' ? stored : defaultMode(role);
}

/**
 * Read the stored preference: per-user key, then the shared legacy fallback.
 * Storage failures (privacy mode) read as null — the role default applies.
 * @param {string|null|undefined} uid
 * @param {any} [storage] defaults to globalThis.localStorage
 * @returns {string|null}
 */
export function readMode(uid, storage) {
  try {
    const st = storage || /** @type {any} */ (globalThis).localStorage;
    return st.getItem(modeKey(uid)) || st.getItem(MODE_KEY_LEGACY) || null;
  } catch {
    return null;
  }
}

/**
 * Persist a toggle to the PER-USER key only (legacy parity: toggleUIMode
 * never wrote the shared fallback key). Never throws.
 *
 * R-030 FALLBACK: returns whether the write actually LANDED. Private-browsing
 * windows, a full quota, and a locked-down shared clinic terminal all make
 * setItem throw (or, in Safari's private mode historically, succeed and then
 * read back nothing). The old signature swallowed that and returned void, so
 * the app could not tell "saved" from "silently discarded" — and the mode chip
 * went on implying the choice would stick. Callers use the boolean to keep the
 * chip and the toast truthful; the in-memory mode applies either way, so a
 * failed write NEVER blocks the toggle or throws.
 *
 * The write is verified by reading back, not assumed from the absence of a
 * throw: a storage that accepts writes and drops them is exactly the case
 * R-030 exists for.
 * @param {string|null|undefined} uid
 * @param {'simple'|'full'} mode
 * @param {any} [storage]
 * @returns {boolean} true when the value is actually retrievable afterwards
 */
export function writeMode(uid, mode, storage) {
  try {
    const st = storage || /** @type {any} */ (globalThis).localStorage;
    const key = modeKey(uid);
    st.setItem(key, mode);
    return st.getItem(key) === mode;
  } catch {
    return false; // storage unavailable — the in-memory mode still applies this session
  }
}

/**
 * Can this browser persist a UI preference at all? A round-trip probe on a
 * throwaway key — the same "write, read back, believe the read" rule as
 * writeMode, so a storage that silently discards writes reads as unavailable
 * rather than as working. Never throws; cleans up after itself.
 * @param {any} [storage]
 * @returns {boolean}
 */
export function storageAvailable(storage) {
  const probeKey = '__tumaini_probe__';
  try {
    const st = storage || /** @type {any} */ (globalThis).localStorage;
    st.setItem(probeKey, '1');
    const back = st.getItem(probeKey) === '1';
    st.removeItem(probeKey);
    return back;
  } catch {
    return false;
  }
}

/**
 * @param {'simple'|'full'} mode
 * @returns {'simple'|'full'} the other mode (legacy toggle line 1342)
 */
export function nextMode(mode) {
  return mode === 'simple' ? 'full' : 'simple';
}

/**
 * Sidebar mode-chip copy (Blueprint R-030: the mode is visibly named, not a
 * mystery toggle). The chip states the CURRENT mode and offers the switch.
 *
 * When persistence is unavailable the chip SAYS SO rather than quietly
 * promising a preference that will be gone at the next refresh. `persisted`
 * defaults to true so every existing caller keeps the legacy label.
 * @param {'simple'|'full'} mode
 * @param {boolean} [persisted] false when the choice cannot be stored
 * @returns {string}
 */
export function modeChipLabel(mode, persisted = true) {
  const base = mode === 'simple' ? 'Simple view · switch' : 'Advanced view · switch';
  return persisted ? base : base + ' (not saved on this device)';
}

/**
 * The chip's tooltip / accessible description. Only interesting in the
 * non-persisting case, where the user deserves the reason and not just the
 * caveat — a nurse in a locked-down kiosk browser should know it is the
 * browser, not a bug she should report.
 * @param {'simple'|'full'} mode
 * @param {boolean} [persisted]
 * @returns {string}
 */
export function modeChipTitle(mode, persisted = true) {
  if (persisted) return mode === 'simple' ? 'Switch to the advanced view' : 'Switch to the simple view';
  return (
    'This browser is not letting Tumaini save preferences (private window, or storage blocked on this device). ' +
    'Your view choice works for this session but will reset when you sign in again.'
  );
}

/**
 * Toggle toast copy — exact legacy wording (v20 line 1365), plus the honest
 * suffix when the choice could not be persisted (R-030).
 * @param {'simple'|'full'} mode the mode just switched TO
 * @param {boolean} [persisted]
 * @returns {string}
 */
export function modeToast(mode, persisted = true) {
  const base = mode === 'simple' ? 'Simple view — advanced tools hidden' : 'Advanced tools shown';
  return persisted ? base : base + ' — not saved on this device';
}
